// src/features/localization/NamespaceTree.tsx
// Left panel of the keys manager — namespace list, scoped by product.
// ListNamespaces (home page's namespace source; CreateNamespace lives on the
// admin Namespaces settings tab, see docs/ux/localization-ia.md §3).

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Alert } from '@react/components';
import { localizationApi } from '@core/api';
import type { TranslationNamespace } from '@core/types/localization';
import { errMessage } from './helpers';

export interface NamespaceTreeProps {
  selected: TranslationNamespace | null;
  onSelect: (ns: TranslationNamespace | null) => void;
}

export function NamespaceTree({ selected, onSelect }: NamespaceTreeProps) {
  const { t } = useI18n();
  const [namespaces, setNamespaces] = useState<TranslationNamespace[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    localizationApi
      .ListNamespaces()
      .then((res) => setNamespaces(res.items?.items ?? []))
      .catch((e) => setError(errMessage(e, t('localization.namespaces.loadError'))))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-1">
      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {t('localization.namespaces.title')}
      </p>
      {error && <Alert variant="error">{error}</Alert>}
      {isLoading && <div className="h-24 animate-pulse rounded-md bg-muted" aria-busy="true" />}
      {!isLoading && (
        <div className="space-y-0.5">
          <button
            type="button"
            onClick={() => onSelect(null)}
            className={`block w-full rounded-md px-2 py-1.5 text-left text-sm ${
              !selected ? 'bg-accent font-medium text-foreground' : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
            }`}
          >
            {t('localization.namespaces.all')}
          </button>
          {namespaces.length === 0 && <p className="px-2 py-1.5 text-sm text-muted-foreground">{t('localization.namespaces.empty')}</p>}
          {namespaces.map((ns) => (
            <button
              key={ns.entity?.id}
              type="button"
              onClick={() => onSelect(ns)}
              className={`block w-full truncate rounded-md px-2 py-1.5 text-left text-sm ${
                selected?.entity?.id === ns.entity?.id ? 'bg-accent font-medium text-foreground' : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
              }`}
            >
              {ns.name}
              {ns.product_slug && <span className="ml-1 text-xs text-muted-foreground">· {ns.product_slug}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
